import RateBars from '@/components/RateBars/RateBars';
import { SVGIcon } from '@/components/SVGIcon/SVGIcon';
import React from 'react';

export interface CardSummaryBoxProps {
  title: string;
  maxRating?: number;
  icon: React.ReactNode;
  rating?: number;
  ratingText: string;
  description: string;
  isOk?: boolean; // if true, hide bar and rating
  requiresAttention?: boolean; // if true, show "Wymaga uwagi" warning state
  targetId?: string; // ID elementu sekcji do której ma prowadzić link "Sprawdź"
}

// Funkcja płynnego przewijania do elementu
const smoothScrollToElement = (elementId: string, offset: number = 100) => {
  const element = document.getElementById(elementId);
  if (element) {
    const elementPosition =
      element.getBoundingClientRect().top + window.pageYOffset;
    const offsetPosition = elementPosition - offset;

    window.scrollTo({
      top: offsetPosition,
      behavior: 'smooth',
    });
  }
};

interface CardSummaryBoxRightSectionProps {
  targetId?: string;
  onCheckClick: (e: React.MouseEvent<HTMLAnchorElement>) => void;
}

const StatusVariant: React.FC<CardSummaryBoxRightSectionProps & { label: string; iconElement: React.ReactNode }> = ({
  targetId,
  onCheckClick,
  label,
  iconElement,
}) => (
  <div className="flex flex-row-reverse gap-2 items-start">
    <div className='ml-4'>
      <div className="max-w-[150px] text-base font-bold leading-4 text-text-primary">
        {label}
      </div>
      <div className="flex justify-between items-center mt-3">
        <a
          href={targetId ? `#${targetId}` : '#'}
          onClick={onCheckClick}
          className="text-xs font-medium text-left underline uppercase text-blue"
        >
          Sprawdź
        </a>
      </div>
    </div>
    {iconElement}
  </div>
);

const RequiresAttentionVariant: React.FC<CardSummaryBoxRightSectionProps> = (props) => (
  <div className="flex flex-col justify-start md:items-end basis-0 justify-center flex-1">
    <div className="flex justify-between items-center">
      <StatusVariant
        {...props}
        label="Wymaga uwagi"
        iconElement={
          <div className="flex h-[54px] w-[54px] items-center justify-center">
            <SVGIcon name="triangle-warn" width={54} height={54} />
          </div>
        }
      />
    </div>
  </div>
);

const IsOkVariant: React.FC<CardSummaryBoxRightSectionProps & { rating?: number; ratingText: string }> = ({
  rating,
  ratingText,
  ...props
}) => (
  <div className="flex flex-col justify-start md:items-end basis-0 justify-center flex-1">
    <div className="flex justify-between items-center">
      <StatusVariant
        {...props}
        label={rating ? ratingText : 'Brak zastrzeżeń'}
        iconElement={
          <div className="flex h-[54px] w-[54px] items-center justify-center">
            <SVGIcon name="check-border" width={54} height={54} />
          </div>
        }
      />
    </div>
  </div >
);

const RatingVariant: React.FC<CardSummaryBoxRightSectionProps & {
  rating: number;
  maxRating: number;
  ratingText: string;
}> = ({ targetId, onCheckClick, rating, maxRating, ratingText }) => (
  <div className="flex flex-col justify-start md:items-end basis-0">
    <div className="flex justify-between items-center">
      <div className="flex flex-row-reverse gap-2 items-start">
        <div>
          <div className="flex gap-2 items-end">
            <span className="text-[40px] font-extrabold leading-[40px] text-text-primary">
              {rating}
            </span>
            <span className="text-[24px] font-normal leading-[32px] text-[#CDD4E0]">
              /{maxRating}
            </span>
          </div>
          <div className="max-w-[150px] text-base font-bold text-text-primary">
            {rating ? ratingText : 'Brak zastrzeżeń'}
          </div>
          <div className="flex justify-between items-center mt-3">
            <a
              href={targetId ? `#${targetId}` : '#'}
              onClick={onCheckClick}
              className="text-xs font-medium text-left underline uppercase text-blue"
            >
              Sprawdź
            </a>
          </div>
        </div>
        <RateBars maxRating={maxRating} rating={rating} />
      </div>
    </div>
  </div>
);

export const CardSummaryBox: React.FC<CardSummaryBoxProps> = ({
  title,
  maxRating = 5,
  icon,
  rating = 0,
  ratingText,
  description,
  isOk = false,
  requiresAttention = false,
  targetId,
}) => {
  const handleCheckClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault();
    if (targetId) {
      smoothScrollToElement(targetId);
    }
  };

  const renderRightSection = () => {
    if (requiresAttention) {
      return <RequiresAttentionVariant targetId={targetId} onCheckClick={handleCheckClick} />;
    }
    if (isOk) {
      return <IsOkVariant targetId={targetId} onCheckClick={handleCheckClick} rating={rating} ratingText={ratingText} />;
    }
    return <RatingVariant targetId={targetId} onCheckClick={handleCheckClick} rating={rating} maxRating={maxRating} ratingText={ratingText} />;
  };

  return (
    <div className="flex flex-row flex-wrap gap-4 px-5 py-7 rounded-2xl border md:justify-between border-grayBorder">
      <div className="flex flex-col flex-1 gap-4 xs:min-w-[300px]">
        <div className="flex gap-2 items-center">
          <div className="flex h-[40px] w-[40px] items-center justify-center rounded-xl bg-gray-100">
            {icon}
          </div>
          <div className="flex-1 text-xl font-bold text-text-primary">
            {title}
          </div>
        </div>
        <p className="mt-1 max-w-full text-sm text-gray-500 text-wrap">{description}</p>
      </div>

      {renderRightSection()}
    </div>
  );
};
